<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import {
  useCoreStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { TransportSelector } from '@apps/main/new-desktop/modules/tranposrtSelector';

import { TransportType } from '@libs/trip-planner-interfaces';
import { isExperiment } from '@libs/feature-toggle';

import type { ItineraryUpsellNoTransportSegment } from '../../models';
import { useTripOverviewPageStore } from '../../stores';

import ItineraryTimeline from './ItineraryTimeline.vue';
import AddFerryCard from './upsell/AddFerryCard.vue';
import AddFlightCard from './upsell/AddFlightCard.vue';
import ItineraryIcon from './ItineraryIcon.vue';
import ExperimentTransportBanner from './upsell/ExperimentTransportBanner.vue';

const { t } = useI18n();

interface ItineraryUpsellNoTransportSegmentProps {
  segment: ItineraryUpsellNoTransportSegment;
}

const props = defineProps<ItineraryUpsellNoTransportSegmentProps>();

const coreStore = useCoreStore();
const statsigEventsStore = useStatsigEventsStore();
const tripOverviewPageStore = useTripOverviewPageStore();

function selectTransport(transport: TransportType): void {
  tripOverviewPageStore.selectTransport(props.segment, transport);
}
</script>

<template>
  <ExperimentTransportBanner
    v-if="isExperiment('transport_banner')"
    :country="segment.destination"
    @add-flight="selectTransport(TransportType.Flight)"
    @add-ferry="selectTransport(TransportType.Ferry)"
    @select-transport="selectTransport($event)"
  />
  <ItineraryTimeline v-else :segment-type="props.segment.segmentType">
    <template #icon>
      <ItineraryIcon :segment-type="props.segment.segmentType" />
    </template>
    <template #header>
      <div class="itinerary-upsell-no-transport-segment-header">
        <div class="title">
          <div class="primary">
            {{ $t('planner.transport.upsell.add_transport') }}
          </div>
          <div class="secondary">
            {{ $t('planner.transport.upsell.convinient_and_safe') }}
          </div>
        </div>
        <TransportSelector
          :transport-type="TransportType.None"
          :initial-option="t('planner.transport.other_transports')"
          :disabled="false"
          is-upsell-no-transport
          @select="selectTransport($event)"
          @open="statsigEventsStore.logTripOverviewTransportSelectorOpened"
        />
      </div>
    </template>

    <template #content>
      <div class="itinerary-upsell-no-transport-segment-content">
        <AddFlightCard
          :travel-guarantee-page="coreStore.getTravelGuaranteePage()"
          @add="selectTransport(TransportType.Flight)"
        />
        <AddFerryCard @add="selectTransport(TransportType.Ferry)" />
      </div>
    </template>
  </ItineraryTimeline>
</template>

<style scoped lang="scss">
.itinerary-upsell-no-transport-segment-header {
  width: 100%;

  display: flex;
  justify-content: space-between;

  .title {
    .primary {
      color: var(--color-secondary-40);
      font: var(--font-label-l-bold);
      margin-bottom: var(--spacing-8);
    }

    .secondary {
      color: var(--color-secondary-60);
      font: var(--font-label-l-light);
    }
  }
}

.itinerary-upsell-no-transport-segment-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-16);
}
</style>
